import I18n from 'I18n';
import { DatePartTypes, DateTruncationTypes, FieldTypes, Formats, UNDEFINED_BUCKET } from '../schema/column-records';
import { getTextContentFromHtml } from 'sanitize-text/sanitizers/TextSanitizer';

// @ts-expect-error migrate upstream file
import formatShortMonthYear from 'I18n/utils/formatShortMonthYear';

// @ts-expect-error migrate upstream file
import { formatDate } from 'reporting-data/hydrate/propertyFormatter';
// @ts-expect-error migrate upstream file
import { formatDuration } from 'reporting-visualizations/formatters/propertyFormatter';
import { getDatePartTransformValue, getDateTruncTransformValue, getFieldType, hasDatePart, hasDateTimeTrunc } from '../utils/column-utils';
const DATE_FORMATS = {
  [DateTruncationTypes.MINUTE]: 'l LT z',
  [DateTruncationTypes.DAY]: 'l',
  [DateTruncationTypes.WEEK]: 'l',
  // for MONTH, use formatShortMonthYear helper for locale-specific formatting
  // (no proper locale-specific format string exists currently)
  [DateTruncationTypes.MONTH]: undefined,
  [DateTruncationTypes.QUARTER]: '[Q]Q YYYY',
  [DateTruncationTypes.YEAR]: 'YYYY'
};
const DATE_PART_FORMATS = {
  [DatePartTypes.DAY]: 'D',
  [DatePartTypes.MONTH]: 'MMM',
  [DatePartTypes.QUARTER]: '[Q]Q',
  [DatePartTypes.WEEK]: 'w',
  [DatePartTypes.YEAR]: 'YYYY',
  [DatePartTypes.DAYOFWEEK]: 'ddd',
  [DatePartTypes.DAYOFYEAR]: 'DDD',
  [DatePartTypes.HOUR]: 'LT'
};
export const DATE_PART_MOMENT_ACCESSORS = {
  [DatePartTypes.DAY]: 'date',
  [DatePartTypes.MONTH]: 'month',
  [DatePartTypes.QUARTER]: 'quarter',
  [DatePartTypes.WEEK]: 'week',
  [DatePartTypes.YEAR]: 'year',
  [DatePartTypes.DAYOFWEEK]: 'day',
  [DatePartTypes.DAYOFYEAR]: 'dayOfYear',
  [DatePartTypes.HOUR]: 'hour'
};
const getDatePartFormatter = column => value => {
  const transformArgument = getDatePartTransformValue(column) || DatePartTypes.DAY;
  const formatString = DATE_PART_FORMATS[transformArgument];
  const momentSetter = DATE_PART_MOMENT_ACCESSORS[transformArgument];
  const valueOffset = transformArgument === DatePartTypes.MONTH ? -1 : 0;
  if (formatString && momentSetter) {
    const momentWithOffset = transformArgument === DatePartTypes.DAY ? I18n.moment().month(0)[momentSetter](value + valueOffset) : I18n.moment()[momentSetter](value + valueOffset);
    return transformArgument === DatePartTypes.HOUR ? momentWithOffset.minute(0).format(formatString) : momentWithOffset.format(formatString);
  }
  return value;
};
const getDateTruncFormatter = column => value => {
  const transformArgument = getDateTruncTransformValue(column) || DateTruncationTypes.DAY;
  const displayTimestamp = transformArgument === DateTruncationTypes.MINUTE;
  const fieldType = getFieldType(column.field);
  const isDateTimestamp = fieldType === FieldTypes.DATE && typeof value === 'number';
  if (displayTimestamp) {
    return I18n.moment.portalTz(value).format(DATE_FORMATS[transformArgument]);
  }

  // DATE type timestamps are stored as UTC, so must be converted to UTC
  const momentValue = isDateTimestamp ? I18n.moment(value).utc() : I18n.moment(value);
  if (transformArgument === DateTruncationTypes.MONTH) {
    return formatShortMonthYear(momentValue);
  }
  return momentValue.format(DATE_FORMATS[transformArgument]);
};
const getDateTimeTruncFormatter = () => value => {
  return formatDate(value, 'lll', 'userTz');
};
const getDateTimeFormatter = column => {
  const formatter = hasDatePart(column) ? getDatePartFormatter(column) : hasDateTimeTrunc(column) ? getDateTimeTruncFormatter() : getDateTruncFormatter(column);
  return value => {
    if (value === UNDEFINED_BUCKET) {
      return undefined;
    }
    const typeSafeValue = isNaN(Number(value)) ? value : Number(value);
    return formatter(typeSafeValue);
  };
};
const getHTMLFormatter = __column => {
  return value => getTextContentFromHtml(value);
};
const getDurationFormatter = __column => {
  return value => formatDuration(value).toString();
};
export const FORMATTERS = {
  [Formats.DATETIME]: getDateTimeFormatter,
  [Formats.DATE]: getDateTimeFormatter,
  [Formats.HTML]: getHTMLFormatter,
  // The formatters below are usually taken care of by the visualization itself
  // via the vis property type, so I've omitted them for now:

  // [FormattingTypes.NUMBER]: getNumberFormatter,
  // [FormattingTypes.BOOLEAN]: getBooleanFormatter,
  // [FormattingTypes.CURRENCY]: getCurrencyFormatter,
  [Formats.DURATION]: getDurationFormatter
};
export const __TESTABLE__ = {
  getDateTimeFormatter
};